/**
 * The ratios a fund is valued on, and how each one adds up across holdings.
 *
 * A price multiple does NOT average — a fund's P/E is covered weight over
 * Σ(weight ÷ P/E), so what a holding contributes is its weight ÷ its multiple,
 * an earnings-yield term. Those terms add, and their sum inverted is the fund's
 * own multiple. A dividend yield or an ROE is already a per-rupee flow, so a
 * holding contributes weight × value and those add directly. `harmonic` says
 * which rule a ratio follows; the roll-up in the strip and the contributions
 * under each row read the same list, so the two cannot disagree about it.
 */
export const VALUATION_METRICS = [
  { key: 'pe', label: 'P/E', pick: (r) => r.peRatio, harmonic: true },
  { key: 'forwardPe', label: 'Forward P/E', pick: (r) => r.forwardPe, harmonic: true },
  { key: 'pb', label: 'P/B', pick: (r) => r.priceBookMrq, harmonic: true },
  { key: 'evEbitda', label: 'EV / EBITDA', pick: (r) => r.enterpriseValueEbitda, harmonic: true },
  { key: 'dividendYield', label: 'Dividend yield', pick: (r) => r.dividendYield, harmonic: false },
  { key: 'roe', label: 'Return on equity', pick: (r) => r.returnOnEquityTtm, harmonic: false },
];

export const valuationMetric = (key) => VALUATION_METRICS.find((m) => m.key === key) ?? null;

/**
 * Which holdings make the fund's figure on one ratio what it is.
 *
 * Each priced row carries its `term` — weight ÷ multiple for a harmonic ratio,
 * weight × value for a yield — largest first, with the covered weight, the sum
 * of the terms and the fund's own figure by the same rule the strip uses. A
 * holding the master did not price on this ratio keeps a null term: it is
 * unpriced, not a zero, and the covered weight says how much of the book the
 * figure speaks for. Rows arrive already normalised, `weight` a fraction and
 * `weightPct` a percentage.
 */
export function valuationContributions(rows, metric) {
  const all = (rows ?? [])
    .map((r) => {
      /* Unpublished is unpriced, not zero: `Number(null)` is 0, which for a
         rate would have counted a holding with no dividend yield on file as
         a real 0% contributor and pulled the fund's figure down. */
      const raw = metric.pick(r);
      const value = raw == null ? NaN : Number(raw);
      const weight = Number(r.weight);
      const usable = Number.isFinite(value) && Number.isFinite(weight) && weight > 0
        && (!metric.harmonic || value > 0);
      return {
        symbol: r.symbol,
        name: r.name,
        sector: r.sector,
        weightPct: r.weightPct,
        value: usable ? value : null,
        term: usable ? (metric.harmonic ? weight / value : weight * value) : null,
        weight: Number.isFinite(weight) ? weight : 0,
      };
    })
    .sort((a, b) => (b.term ?? -Infinity) - (a.term ?? -Infinity));

  const priced = all.filter((r) => r.term != null);
  const covered = priced.reduce((t, r) => t + r.weight, 0);
  const total = priced.reduce((t, r) => t + r.term, 0);
  return {
    /* `share` is the holding's slice of the fund's figure, in percent of the
       whole — the terms are what add, so a share of the terms is a share of
       the figure, and the column sums to 100 over the priced holdings. */
    rows: all.map((r) => ({
      ...r,
      share: r.term == null || !total ? null : (r.term / total) * 100,
    })),
    priced: priced.length,
    covered,
    total,
    fund: metric.harmonic ? (total > 0 ? covered / total : null) : (covered > 0 ? total / covered : null),
  };
}
